import {
	addDoc,
	collection,
	doc,
	serverTimestamp,
	updateDoc,
} from "firebase/firestore";
import React, { useState } from "react";

import ConfirmData from "./ConfirmData";
import { authClasses } from "../../../pages/Auth/authClasses";
import { db } from "../../../firebase";
import { getRegisteredSessionDisplayLabel, doesSessionMatchRegistration } from "../eventSchedule";

const RegisterPopup = ({
	athlete,
	evnt,
	setRegisterEvent,
	addEventRegistration,
	setPayEvent,
}) => {
	const { buttonSmall } = authClasses;

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const [gradYear, setGradYear] = useState(
		athlete.gradYear !== undefined ? String(athlete.gradYear) : "HS Grad Year",
	);
	const [school, setSchool] = useState(athlete.school ?? undefined);
	const [month, setMonth] = useState(
		athlete.dob !== undefined
			? athlete.dob.toDate().toLocaleString("default", { month: "long" })
			: "Month",
	);
	const [day, setDay] = useState(
		athlete.dob !== undefined ? String(athlete.dob.toDate().getDate()) : "Day",
	);
	const [year, setYear] = useState(
		athlete.dob !== undefined
			? String(athlete.dob.toDate().getFullYear())
			: "Year",
	);
	const [inches, setInches] = useState(
		athlete.inches !== undefined ? String(athlete.inches) : "Height (in)",
	);
	const [feet, setFeet] = useState(
		athlete.feet !== undefined ? String(athlete.feet) : "Height (ft)",
	);
	const [shirtSize, setShirtSize] = useState(athlete.shirtSize ?? "Shirt Size");
	const [shortSize, setShortSize] = useState(
		athlete.shortSize ?? "Shorts Size",
	);
	const [isPracticeTeam, setIsPracticeTeam] = useState(null);
	const isFreeEvent = evnt.cost === 0 || evnt.cost === "0";
	const missingRegistrationRequirement =
		(evnt.multipleSessions && !(evnt.sessions ?? []).some(session => doesSessionMatchRegistration(session, evnt.session))) ||
		school === undefined ||
		isNaN(parseInt(gradYear)) ||
		isNaN(parseInt(day)) ||
		isNaN(parseInt(year)) ||
		month === "Month" ||
		isNaN(parseInt(feet)) ||
		isNaN(parseInt(inches)) ||
		shirtSize === "Shirt Size" ||
		shortSize === "Shorts Size" ||
		(evnt.practiceTeams === true && isPracticeTeam === null);
	const missingRegistrationMessage =
		evnt.multipleSessions && !(evnt.sessions ?? []).some(session => doesSessionMatchRegistration(session, evnt.session))
			? "Choose a session before registering."
			: school === undefined
			? "Missing school"
			: isNaN(parseInt(gradYear))
				? "Missing grad year"
				: isNaN(parseInt(day)) || isNaN(parseInt(year)) || month === "Month"
					? "Incomplete date of birth"
					: isNaN(parseInt(feet)) || isNaN(parseInt(inches))
						? "Incomplete height"
						: shirtSize === "Shirt Size"
							? "Missing shirt size"
							: shortSize === "Shorts Size"
								? "Missing shorts size"
								: evnt.practiceTeams === true && isPracticeTeam === null
									? "Select travel or practice team"
									: "";

	const handleRegister = async () => {
		if (missingRegistrationRequirement) {
			return;
		}

		setLoading(true);
		const colRef = collection(db, "registrations");

		try {
			const athRef = doc(
				db,
				`users/${athlete.parentID}/athletes/${athlete.id}`,
			);
			await updateDoc(athRef, {
				gradYear,
				school,
				inches,
				feet,
				dob: new Date(`${month} ${day}, ${year}`),
				shirtSize,
				shortSize,
			});

			let regData = {
				athlete: athlete.id,
				event: evnt.id,
				status: isFreeEvent ? "paid" : "unpaid",
				parentID: athlete.parentID,
			};

			if (evnt.session) {
				regData.session = evnt.session;
			}

			if (isFreeEvent) {
				regData.paidAt = serverTimestamp();
			}

			if (evnt.practiceTeams) {
				regData.isPracticeTeam = isPracticeTeam;
			}

			const ref = await addDoc(colRef, regData);

			setLoading(false);
			setRegisterEvent(null);
			addEventRegistration(
				evnt.id,
				athlete.id,
				ref.id,
				isFreeEvent ? "paid" : "unpaid",
				evnt.practiceTeams ? isPracticeTeam : null,
				evnt.session,
			);

			if (!isFreeEvent) {
				setPayEvent(evnt);
			}
		} catch (err) {
			setError(err);
			setLoading(false);
		}
	};

	return (
		<div className="fixed inset-0 z-20 flex items-center justify-center overflow-y-auto bg-black/75 px-4 py-6">
			<div className="grid max-h-[92vh] w-full max-w-3xl gap-5 overflow-y-auto rounded-lg border border-white/10 bg-[#24262c] p-5 shadow-2xl shadow-black/40">
				{error ? (
					// Submit Error
					<div className="grid gap-3">
						<h3 className="m-0 text-2xl text-white">Error Registering</h3>
						<p className="m-0 text-sm text-slate-300">
							Email director@sidewinderva.com if this keeps happening.
						</p>
						<small className="rounded-md border border-red-300/30 bg-red-500/15 p-3 text-red-100">
							{error.toString()}
						</small>
						<button
							className={buttonSmall}
							onClick={() => {
								setError(null);
								setRegisterEvent(null);
							}}
						>
							Ok
						</button>
					</div>
				) : (
					<div className="grid gap-5">
						{/* Event Information Display */}
						<div className="grid gap-2 border-b border-white/10 pb-4">
							<span className="text-xs font-bold uppercase tracking-[0.18em] text-accent-color-2">
								Register
							</span>
							<h3 className="m-0 text-2xl text-white">{evnt.title}</h3>
							{evnt.session ? <p className="m-0 text-sm text-slate-300">Session: {getRegisteredSessionDisplayLabel(evnt.sessions ?? [], evnt.session)}</p> : null}
							<div className="flex flex-wrap gap-2 text-sm text-slate-300">
								<span>{evnt.dateString}</span>
								<span>·</span>
								<span>{evnt.location}</span>
								<span>·</span>
								<span>${evnt.cost}</span>
							</div>
							<p className="m-0 text-sm text-slate-300">
								Athlete:{" "}
								<strong className="text-white">
									{athlete.firstName} {athlete.lastName}
								</strong>
							</p>
							{evnt.type !== "Tryout" ? (
								<p className="m-0 text-sm text-slate-400">
									{evnt.description}
								</p>
							) : null}
						</div>

						{/* Confirm Athlete Data Fields */}
						<ConfirmData
							school={school}
							setSchool={setSchool}
							gradYear={gradYear}
							setGradYear={setGradYear}
							month={month}
							setMonth={setMonth}
							day={day}
							setDay={setDay}
							year={year}
							setYear={setYear}
							feet={feet}
							setFeet={setFeet}
							inches={inches}
							setInches={setInches}
							season={athlete.season}
							shortSize={shortSize}
							setShortSize={setShortSize}
							shirtSize={shirtSize}
							setShirtSize={setShirtSize}
							practiceTeams={evnt.practiceTeams}
							isPracticeTeam={isPracticeTeam}
							setIsPracticeTeam={setIsPracticeTeam}
						/>

						{/* Buttons */}
						<div className="flex flex-wrap justify-end gap-3 border-t border-white/10 pt-4">
							{/* Cancel Button */}
							<button
								className={`${buttonSmall} border-white/10 bg-[#333333] hover:bg-[#3f3f3f]`}
								disabled={loading}
								onClick={() => setRegisterEvent(null)}
							>
								Cancel
							</button>
							{/* REGISTER BUTTON */}
							<button
								className={buttonSmall}
								onClick={() => handleRegister()}
								disabled={loading || missingRegistrationRequirement}
							>
								{loading ? "Registering..." : "Register"}
							</button>
						</div>

						{/* Error Conditions */}
						{missingRegistrationRequirement ? (
							// Error Display
							<small className="text-center text-yellow-100">
								{missingRegistrationMessage}
							</small>
						) : null}
					</div>
				)}
			</div>
		</div>
	);
};

export default RegisterPopup;
